import { uiText, useUiLanguage } from '../../../utils/uiTranslation';
import React, { useEffect, useMemo, useState } from 'react';
import { ANALYTICS_GRAPH_TYPES } from './graphCatalog';

const formatValue = (value, format) => {
  if (value === null || value === undefined || value === '') return '—';
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '—';
  if (format === 'percent') return `${numeric.toFixed(1)}%`;
  if (format === 'days') return uiText("{{count}} days", { count: numeric.toFixed(1) });
  if (format === 'score') return numeric.toFixed(1);
  return numeric.toLocaleString();
};

const getPerformanceTone = (progress) => {
  if (progress >= 0.68) return 'success';
  if (progress >= 0.36) return 'warning';
  return 'danger';
};

const getConditionalCellStyle = (progress) => {
  const bounded = Math.max(0, Math.min(progress, 1));
  const tone = getPerformanceTone(bounded);
  const rgbMap = {
    success: '45, 184, 122',
    warning: '212, 146, 10',
    danger: '226, 83, 74'
  };
  const rgb = rgbMap[tone];
  return {
    background: `linear-gradient(180deg, rgba(${rgb}, ${(0.16 + (bounded * 0.22)).toFixed(2)}), rgba(${rgb}, ${(0.28 + (bounded * 0.42)).toFixed(2)}))`,
    borderColor: `rgba(${rgb}, ${(0.18 + (bounded * 0.42)).toFixed(2)})`,
    boxShadow: `inset 0 0 0 1px rgba(${rgb}, ${(0.16 + (bounded * 0.36)).toFixed(2)})`
  };
};

const getHeatCellStyle = (progress) => {
  const bounded = Math.max(0, Math.min(progress, 1));
  const weighted = Math.pow(bounded, 0.78);
  const lightness = 95 - (weighted * 68);
  const saturation = 34 + (weighted * 54);
  return {
    background: `hsl(221 ${saturation.toFixed(1)}% ${lightness.toFixed(1)}%)`,
    color: weighted > 0.44 ? 'rgba(var(--app-ink-rgb, 255, 255, 255), 0.98)' : 'rgba(3, 17, 40, 0.9)'
  };
};

const MatrixVisual = ({ model }) => {
  useUiLanguage();
  const rootDataset = model.dataset;
  const [selectedViewId, setSelectedViewId] = useState(rootDataset?.selectedViewId || rootDataset?.selectors?.[0]?.id || '');

  useEffect(() => {
    const nextDefault = rootDataset?.selectedViewId || rootDataset?.selectors?.[0]?.id || '';
    if (!nextDefault) return;
    setSelectedViewId((currentId) => {
      if (!rootDataset?.views?.[currentId] || currentId !== nextDefault) {
        return nextDefault;
      }
      return currentId;
    });
  }, [rootDataset]);

  const activeView = rootDataset?.views?.[selectedViewId] || rootDataset;

  const rows = useMemo(() => activeView?.rows || [], [activeView?.rows]);
  const columns = useMemo(() => activeView?.columns || [], [activeView?.columns]);
  const cells = useMemo(() => activeView?.cells || [], [activeView?.cells]);
  const onSelectCategory = model.options?.onSelectCategory;
  const gridTemplateColumns = `1.15fr repeat(${columns.length}, minmax(0, 0.8fr))`;

  const cellMap = useMemo(
    () =>
      cells.reduce((accumulator, cell) => {
        accumulator[`${cell.rowId}:${cell.columnId}`] = cell.value;
        return accumulator;
      }, {}),
    [cells]
  );

  const columnExtents = useMemo(
    () =>
      columns.reduce((accumulator, column) => {
        const values = rows
          .map((row) => cellMap[`${row.id}:${column.id}`])
          .filter((value) => value !== null && value !== undefined && value !== '')
          .map(Number)
          .filter(Number.isFinite);
        accumulator[column.id] = {
          min: Math.min(...values, 0),
          max: Math.max(...values, 1)
        };
        return accumulator;
      }, {}),
    [cellMap, columns, rows]
  );

  if (!rows.length || !columns.length) return null;

  if ((model.size || 'medium') === 'compact') {
    const primaryColumn = columns[0];
    const rankedRows = [...rows]
      .map((row) => ({
        ...row,
        value: cellMap[`${row.id}:${primaryColumn.id}`]
      }))
      .sort((left, right) => (Number(right.value) || 0) - (Number(left.value) || 0));

    return (
      <div className="analytics-visual analytics-visual-scatter-list">
        {rankedRows.map((row) => (
          <div key={row.id} className="analytics-visual-scatter-list-row">
            <div className="analytics-visual-scatter-list-name">
              <span className="analytics-visual-legend-swatch accent" />
              <span className="analytics-visual-label">{uiText(row.label)}</span>
            </div>
            <div className="analytics-visual-scatter-list-metrics">
              <strong>{formatValue(row.value, primaryColumn.format)}</strong>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (model.activeGraph === ANALYTICS_GRAPH_TYPES.TABLE || model.activeGraph === ANALYTICS_GRAPH_TYPES.CONDITIONAL_TABLE) {
    return (
      <div className="analytics-visual analytics-visual-matrix">
        {rootDataset?.selectors?.length ? (
          <div className="analytics-visual-source-picker" role="tablist" aria-label={rootDataset.selectorLabel || uiText("Selector")}>
            {rootDataset.selectors.map((option) => (
              <button
                key={option.id}
                type="button"
                className={`analytics-visual-source-pill${selectedViewId === option.id ? ' active' : ''}`}
                onClick={() => setSelectedViewId(option.id)}
              >
                {uiText(option.label)}
              </button>
            ))}
          </div>
        ) : null}
        <div className="analytics-visual-matrix-head" style={{ gridTemplateColumns }}>
          <span className="analytics-visual-table-head-label" />
          {columns.map((column) => (
            <span key={column.id} className="analytics-visual-table-head-cell">{uiText(column.label)}</span>
          ))}
        </div>
        {rows.map((row) => (
          <div
            key={row.id}
            className={`analytics-visual-matrix-row analytics-visual-matrix-row-flat${onSelectCategory ? ' clickable' : ''}`}
            onClick={onSelectCategory ? () => onSelectCategory(row.id) : undefined}
            onKeyDown={onSelectCategory ? (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onSelectCategory(row.id);
              }
            } : undefined}
            role={onSelectCategory ? 'button' : undefined}
            tabIndex={onSelectCategory ? 0 : undefined}
            style={{ gridTemplateColumns }}
          >
            <span className="analytics-visual-table-row-label">{uiText(row.label)}</span>
            {columns.map((column) => {
              const rawValue = cellMap[`${row.id}:${column.id}`];
              const value = Number(rawValue);
              const hasNumericValue = rawValue !== null && rawValue !== undefined && rawValue !== '' && Number.isFinite(value);
              if (model.activeGraph === ANALYTICS_GRAPH_TYPES.TABLE) {
                return <span key={column.id} className="analytics-visual-table-cell">{formatValue(rawValue, column.format)}</span>;
              }

              if (!hasNumericValue) {
                return (
                  <span key={column.id} className="analytics-visual-conditional-cell">
                    {formatValue(rawValue, column.format)}
                  </span>
                );
              }

              const { min, max } = columnExtents[column.id] || { min: 0, max: 1 };
              const range = Math.max(max - min, 1);
              const rawProgress = (value - min) / range;
              const progress = column.direction === 'asc' ? 1 - rawProgress : rawProgress;
              const boundedProgress = Math.max(0, Math.min(progress, 1));
              return (
                <span
                  key={column.id}
                  className="analytics-visual-conditional-cell"
                  style={getConditionalCellStyle(boundedProgress)}
                >
                  {formatValue(value, column.format)}
                </span>
              );
            })}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="analytics-visual analytics-visual-matrix-heatmap">
      {rootDataset?.selectors?.length ? (
        <div className="analytics-visual-source-picker" role="tablist" aria-label={rootDataset.selectorLabel || uiText("Selector")}>
          {rootDataset.selectors.map((option) => (
            <button
              key={option.id}
              type="button"
              className={`analytics-visual-source-pill${selectedViewId === option.id ? ' active' : ''}`}
              onClick={() => setSelectedViewId(option.id)}
            >
              {uiText(option.label)}
            </button>
          ))}
          </div>
        ) : null}
      <div className="analytics-visual-heatmap-head" style={{ gridTemplateColumns }}>
        <span className="analytics-visual-heatmap-corner" />
        {columns.map((column) => (
          <span key={column.id} className="analytics-visual-heat-column-label">{uiText(column.label)}</span>
        ))}
      </div>
      {rows.map((row) => {
        return (
          <div
            key={row.id}
            className={`analytics-visual-heatmap-row${onSelectCategory ? ' clickable' : ''}`}
            onClick={onSelectCategory ? () => onSelectCategory(row.id) : undefined}
            onKeyDown={onSelectCategory ? (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onSelectCategory(row.id);
              }
            } : undefined}
            role={onSelectCategory ? 'button' : undefined}
            tabIndex={onSelectCategory ? 0 : undefined}
            style={{ gridTemplateColumns }}
          >
            <span className="analytics-visual-heat-row-label">{uiText(row.label)}</span>
            {columns.map((column) => {
              const rawValue = cellMap[`${row.id}:${column.id}`];
              const value = Number(rawValue);
              const hasNumericValue = rawValue !== null && rawValue !== undefined && rawValue !== '' && Number.isFinite(value);
              if (!hasNumericValue) {
                return (
                  <span key={column.id} className="analytics-visual-heat-cell">
                    {formatValue(rawValue, column.format)}
                  </span>
                );
              }
              const { min, max } = columnExtents[column.id] || { min: 0, max: 1 };
              const range = Math.max(max - min, 1);
              const rawProgress = (value - min) / range;
              const progress = column.direction === 'asc' ? 1 - rawProgress : rawProgress;
              return (
                <span
                  key={column.id}
                  className="analytics-visual-heat-cell"
                  style={getHeatCellStyle(progress)}
                >
                  {formatValue(value, column.format)}
                </span>
              );
            })}
          </div>
        );
      })}
    </div>
  );
};

export default MatrixVisual;
